// Every call the browser makes to the server.
import type { Episode, EpisodeSummary, MotionFrame, Picture } from "../model/episode";
import type { Painted } from "../library/build";
import type { TimelineEpisode } from "../model/timeline";
import type { Asset, AssetKind, Library } from "../model/library";
import type { Emotion, FaceSpots } from "../model/face";
import type { SpeechVoice } from "../model/speech";
import { cleanTake, type TakeResult } from "../audio/clean";
import type { MotionJob } from "../server/motion";
import type { PaintRef, ShotFaceFound } from "../server/paint";
import type { PaintStyle } from "../model/styles";
import type { FaceSprites } from "../model/faceStyles";
import { swapSharper } from "../library/images";
import { paintChoice } from "./common/ModelSelect";
import { goSignIn } from "./account";

// A failed answer as an error. A guest asking for something that costs money gets 401 with `signIn` (worker/index.ts):
// then it's off to Google, coming back to this page.
function failure(res: Response, body: { error?: string; signIn?: string }) {
  if (res.status === 401 && body.signIn) goSignIn(body.signIn);
  return Object.assign(new Error(body.error ?? `Request failed (${res.status})`), { status: res.status });
}
async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw failure(res, body);
  return body as T;
}
// Slow work (painting, motion, voices, the assistant) starts a job on the server and answers `{ jobId }` at once
// (src/server/jobs.ts). This asks how it's going, every 1.5 s at first and less often as it goes on, and gives back
// the result, or throws the job's error. A few failed checks in a row (a dropped connection) are retried.
type JobState<T> = { status: "running" } | { status: "done"; result: T } | { status: "failed"; error?: string; errorStatus?: number };
export async function runJob<T>(start: Promise<{ jobId: string }>): Promise<T> {
  const { jobId } = await start;
  let wait = 1500;
  for (let misses = 0; ; ) {
    await new Promise(r => setTimeout(r, wait));
    wait = Math.min(wait * 1.25, 5000);
    let job: JobState<T>;
    try { job = await call<JobState<T>>(`/api/jobs/${jobId}`); } catch (e) {
      if ((e as { status?: number }).status === 404 || ++misses > 5) throw e;
      continue;
    }
    misses = 0;
    if (job.status === "done") return job.result;
    if (job.status === "failed") throw Object.assign(new Error(job.error ?? "Something went wrong."), { status: job.errorStatus ?? 502 });
  }
}
const slow = <T>(url: string, init?: RequestInit) => runJob<T>(call<{ jobId: string }>(url, init));
const send = (method: string, body: unknown): RequestInit => ({ method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
const wavBody = (wav: Blob): RequestInit => ({ method: "POST", headers: { "content-type": "audio/wav" }, body: wav });

// The AI voice's raw audio (the server passes it through from the speech model), or the server's error.
async function callAudio(url: string, init: RequestInit): Promise<Blob> {
  const res = await fetch(url, init);
  if (!res.ok) {
    throw failure(res, await res.json().catch(() => ({})));
  }
  return res.blob();
}
// A take is cleaned here in the browser (src/audio/clean.ts), then the server stores the finished WAV.
async function saveCleaned<T extends { audio: string }>(raw: Blob, url: string): Promise<T & TakeResult> {
  const c = await cleanTake(raw);
  const saved = await call<T>(url, wavBody(c.wav));
  return { ...saved, duration: c.duration, start: c.start };
}

// Nano Banana Lite pictures come with `sharper`: the id of a job making a sharper copy that replaces the file soon
// (src/server/paint.ts). Once it says it has, the picture is reloaded. The id isn't kept.
async function sharpens<T extends { src: string }>(p: Promise<T & { sharper?: string }>): Promise<T> {
  const { sharper, ...pic } = await p;
  if (sharper) void runJob<{ sharper?: boolean }>(Promise.resolve({ jobId: sharper })).then(r => { if (r.sharper) swapSharper(pic.src); }, () => {});
  return pic as unknown as T;
}
export type MotionAsk = Omit<MotionJob, "model">;

type SpeakBody = { text: string; speech: SpeechVoice; emotion: Emotion; direction?: string };

export const api = {
  library: () => call<Library>("/api/library"),
  createAsset: (a: Asset) => call<Asset>("/api/library/assets", send("POST", a)),
  saveAsset: (a: Asset) => call<Asset>(`/api/library/assets/${a.id}`, send("PUT", a)),
  deleteAsset: (id: string) => call<{ ok: true }>(`/api/library/assets/${id}`, { method: "DELETE" }),
  // Painting goes to the model picked in the "Draw with" dropdown.
  // `refs`: pictures to look at while painting (the image maker's references). `style`: the drawing style's words (the default if left out).
  paint: (kind: AssetKind, description: string, refs: PaintRef[] = [], style?: string) => sharpens(slow<Painted & { faceGuessed?: true }>("/api/library/paint", send("POST", { kind, description, refs, style, ...paintChoice() }))),
  // Change something about a picture that's already made: it goes to the model as the input. A character's face is a
  // best guess (`faceGuessed`) when the drawing didn't mark where it goes.
  editAsset: (kind: AssetKind, src: string, instruction: string, face?: FaceSpots, refs: PaintRef[] = [], style?: string) =>
    sharpens(slow<Painted & { faceGuessed?: true }>("/api/library/paint", send("POST", { kind, edit: { src, instruction, face }, refs, style, ...paintChoice() }))),
  styleSample: (style: string) => sharpens(slow<Picture>("/api/library/styles/sample", send("POST", { style, ...paintChoice() }))),
  styleFaces: (id: string, style: string) => slow<{ sprites: FaceSprites; failed: string[] }>("/api/library/styles/faces", send("POST", { id, style })),
  saveStyles: (styles: PaintStyle[]) => call<PaintStyle[]>("/api/library/styles", send("PUT", { styles })),
  // dots: find the characters' faces from face dots; `faces` then says where each one is (by reference picture).
  paintShot: (refs: PaintRef[], description: string, style?: string, dots?: boolean) =>
    sharpens(slow<Picture & { faces?: ShotFaceFound[] }>("/api/paint-shot", send("POST", { refs, description, style, dots, ...paintChoice() }))),
  editShot: (src: string, refs: PaintRef[], instruction: string, style?: string, dots?: boolean) =>
    sharpens(slow<Picture & { faces?: ShotFaceFound[] }>("/api/paint-shot", send("POST", { refs, edit: { src, instruction }, style, dots, ...paintChoice() }))),
  // Making a shot move (src/server/routes/shots.ts).
  motionGrid: (job: MotionAsk) => slow<{ frames: MotionFrame[] }>("/api/motion/grid", send("POST", { ...job, ...paintChoice() })),

  list: () => call<EpisodeSummary[]>("/api/episodes"),
  get: (id: string) => call<Episode>(`/api/episodes/${id}`),
  // Either kind of episode: a v6 timeline episode has `kind: "timeline"` (isTimelineEpisode).
  chat: (id: string, messages: { role: "user" | "assistant"; content: string }[]) => slow<{ reply: string }>(`/api/episodes/${id}/chat`, send("POST", { messages })),
  getEpisode: (id: string) => call<Episode | TimelineEpisode>(`/api/episodes/${id}`),
  demo: () => call<TimelineEpisode>("/api/demo", send("POST", {})),
  create: () => call<TimelineEpisode>("/api/episodes", send("POST", {})),
  // `rev`: the saved version this copy started from. The server refuses (409) if the file has been saved since.
  save: (ep: TimelineEpisode, rev: number) => call<{ ok: true; rev: number }>(`/api/episodes/${ep.id}`, { ...send("PUT", ep), headers: { "content-type": "application/json", "x-base-rev": String(rev) } }),
  remove: (id: string) => call<{ ok: true }>(`/api/episodes/${id}`, { method: "DELETE" }),
  // A line's voice: a recording, or the AI voice saying the typed text. Both are cleaned here, then saved.
  uploadTake: (epId: string, lineId: string, blob: Blob) => saveCleaned<{ audio: string }>(blob, `/api/episodes/${epId}/lines/${lineId}/audio`),
  speakLine: async (epId: string, lineId: string, body: SpeakBody) =>
    saveCleaned<{ audio: string }>(await callAudio(`/api/episodes/${epId}/lines/${lineId}/speak`, send("POST", body)), `/api/episodes/${epId}/lines/${lineId}/audio`),

  voiceSample: (blob: Blob) => saveCleaned<{ audio: string; url: string }>(blob, "/api/voice-sample"),
  speakSample: async (body: SpeakBody) => saveCleaned<{ audio: string; url: string }>(await callAudio("/api/voice-sample/speak", send("POST", body)), "/api/voice-sample"),

};

export const audioUrl = (epId: string, file: string) => `/data/v2/episodes/${epId}/${file}`;
